import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { ChevronDown } from "lucide-react";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";
import HeroSection from "./components/HeroSection";
import BrandSection from "./components/BrandSection";
import AccidentalDamageGrid from "./components/AccidentalDamageGrid";
import ValueSection from "./components/ValueSection";
import HowItWorks from "./components/HowItWorks";
import NotCovered from "./components/NotCovered";
import HowToBuy from "./components/HowToBuy";

type FaqItem = {
  q: string;
  a: string;
};

const FAQS: FaqItem[] = [
  {
    q: "What is a MecTURING Accidental Care Plan?",
    a: "MecTURING Care provides accidental damage coverage, extended warranty coverage and peace of mind. We get that accidents happen. That’s why with MecTURING Care, we’ll provide you with a replacement product should you need one. The enhanced coverage begins at the time of purchase, and covers until the end of the MecTURING Plan term length. See Terms and Conditions for additional details.",
  },
  {
    q: "Why should I add Protega Product Protection?",
    a: "Protega provides coverage for accidental damage not covered by our manufacturer’s warranty starting on day one. After your manufacturer warranty expires, you’re still covered in case of broken and water spill for the remainder of your Protega coverage term.",
  },
  {
    q: "What happens if I return my product?",
    a: "If you return your product, MecTURING returns the amount you paid for your product protection. Depending on how much time has passed since your purchase, you may receive a prorated refund, less any paid service.",
  },
  {
    q: "I haven’t received a refund for my protection even though I returned my product.",
    a: "MecTURING handles all transactions for product protection sales and refunds. For assistance with refunds, contact our Customer Care.",
  },
  {
    q: "Is this plan the same as warranty or insurance?",
    a: "No. It is an optional protection plan for accidental events, separate from any manufacturer warranty or insurance.",
  },
];

const AccordionItem: React.FC<{
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>("0px");

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      setMaxH(`${el.scrollHeight}px`);
      const t = setTimeout(() => setMaxH("none"), 300);
      return () => clearTimeout(t);
    } else {
      if (el.scrollHeight && maxH === "none") {
        setMaxH(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMaxH("0px"));
        });
      } else {
        setMaxH("0px");
      }
    }
  }, [isOpen]);

  return (
    <div className="tw-border-b tw-text-left tw-px-6 tw-border-gray-200">
      <button
        aria-expanded={isOpen}
        aria-controls={`faq-${index}`}
        onClick={() => onToggle(index)}
        className="tw-w-full tw-text-left tw-py-4 tw-flex tw-justify-between tw-items-start tw-gap-4 "
      >
        <span className="tw-text-lg tw-font-medium">{item.q}</span>
        <ChevronDown
          strokeWidth={1.67}
          className={`tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 ${
            isOpen ? "tw-rotate-180" : "tw-rotate-0"
          }`}
        />
      </button>

      <div
        id={`faq-${index}`}
        ref={contentRef}
        style={{
          maxHeight: maxH === "none" ? undefined : maxH,
          transition: "max-height 300ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
        }}
        className="tw-pr-6 tw-text-sm tw-text-gray-700"
        aria-hidden={!isOpen}
      >
        {/* padding lives inside the collapsible so closed rows stay compact */}
        <div className="tw-pt-1 tw-pb-4">{item.a}</div>
      </div>
    </div>
  );
};

const MecturingCare: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* hero section */}
        <HeroSection />

        {/* brand section */}
        <BrandSection />

        {/* accidental damage protection */}
        <AccidentalDamageGrid />

        {/* value section */}
        <ValueSection />

        {/* how it works */}
        <HowItWorks />

        {/* not covered */}
        <NotCovered />

        {/* how to buy */}
        <HowToBuy />

        {/* FAQ */}
        <section className="tw-bg-white tw-text-black tw-pt-20 tw-pb-24">
          <div className="tw-w-[95%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-3xl tw-font-semibold tw-mb-10">
              MecTURING Accidental Care Plan FAQs
            </h2>
            <div className="tw-max-w-4xl tw-mx-auto tw-mb-20 tw-shadow-sm tw-border tw-border-gray-100 tw-rounded">
              {FAQS.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggleIndex}
                />
              ))}
            </div>
            <Link
              to={"/mecturing-productprotection/tnc"}
              className="hover:tw-underline tw-font-medium hover:tw-text-blue-700"
            >
              Terms and Conditions
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default MecturingCare;
